import React, { useMemo, useRef } from "react";
import { useState, useEffect } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import CurrencyData from "json/currencies.json";
import useClickOutSideComponent from "../../../hooks/useClickOutsideComponent";

interface Currency {
  name: string;
  code: string;
  flag: string;
}

interface Props {
  onSelect: (currency: Currency) => void;
  value?: string;
  error?: string;
  labelClassName?: string;
  labelText?: string;
  isRequired?: boolean;
  isDropdownAbove?: boolean;
}

const CurrencyDropdown: React.FC<Props> = ({
  onSelect,
  value,
  error,
  labelClassName,
  labelText,
  isRequired,
  isDropdownAbove,
}) => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null
  );
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useClickOutSideComponent(containerRef, () => setShowDropdown(false));

  useEffect(() => {
    setCurrencies(CurrencyData);
  }, []);

  const handleSelect = (currency: Currency) => {
    setSelectedCurrency(currency);
    setShowDropdown(false);
    onSelect(currency);
  };

  useEffect(() => {
    if (value && currencies.length > 0) {
      const currency = currencies.find((c) => c.code === value);
      if (currency) {
        setSelectedCurrency(currency);
      }
    }
  }, [value, currencies]);

  return (
    <div className='relative mt-1' ref={containerRef}>
      {labelText && (
        <label
          className={` font-medium capitalize text-sm ${
            showDropdown ? "text-blueShades-4" : "text-ashShade-16"
          } ${labelClassName}`}
        >
          {labelText} {isRequired && <span className='text-ERROR'>*</span>}
        </label>
      )}
      <div
        className={`border cursor-pointer  ${showDropdown ? "border-[1.5px] border-blueShades-6 outline outline-[3px] outline-[#D8E9F080]" : error ? "border-[1.5px] border-ERROR outline outline-3px outline-[#FFE5E5]" : "border-blueShades-2"} rounded-md px-3 py-2 text-sm text-ashShade-16`}
        onClick={() => setShowDropdown(!showDropdown)}
      >
        {selectedCurrency ? (
          <div className='flex items-center justify-between'>
            <div className='text-sm flex items-center'>
              <img
                src={selectedCurrency.flag}
                alt='flag'
                className='w-6 h-4 mr-2'
              />
              <span>{selectedCurrency.code}</span>
            </div>
            {showDropdown ? (
              <IoIosArrowUp size={16} className='text-ashShade-2' />
            ) : (
              <IoIosArrowDown size={16} className='text-ashShade-2' />
            )}
          </div>
        ) : (
          <p className='text-bash !font-SatoshiRegular text-base w-full justify-between flex items-center'>
            <span className='text-sm'>Select Currency</span>
            {showDropdown ? (
              <IoIosArrowUp size={16} className='text-ashShade-2' />
            ) : (
              <IoIosArrowDown size={16} className='text-ashShade-2' />
            )}
          </p>
        )}
      </div>

      {showDropdown && (
        <div
          className={`absolute bg-white border rounded-md w-full z-10 shadow-md ${isDropdownAbove ? "bottom-full" : "top-full"}`}
        >
          {currencies.map((currency) => (
            <div
              key={currency.code}
              className={`py-2 px-3 flex items-center  hover:bg-bbg cursor-pointer font-SatoshiRegular text-base ${selectedCurrency?.code === currency.code && "bg-ashShade-13"}`}
              onClick={() => handleSelect(currency)}
            >
              <img
                src={currency.flag}
                alt='flag'
                className='w-6 h-4 mr-2 rounded-[4px]'
              />
              {currency.code}
            </div>
          ))}
        </div>
      )}
      {error && (
        <span className='absolute left-0 top-full text-ERROR text-[13px] mb-4'>
          {error}
        </span>
      )}
    </div>
  );
};

export default CurrencyDropdown;
